import api from './api';
import { nullableUuid } from '@/utils/uuid';

export type OrdemProducaoStatus = 'ABERTA' | 'LIBERADA' | 'EM_PRODUCAO' | 'PAUSADA' | 'ENCERRADA' | 'CANCELADA';

export interface OrdemProducaoMaterial {
  id: string;
  produto_nome: string;
  produto_codigo: string;
  quantidade_prevista: number;
  quantidade_consumida: number;
  unidade: string;
  variacao: number;
}

export interface OrdemProducaoOperacao {
  id: string;
  sequencia: number;
  operacao: string;
  centro_trabalho_nome: string;
  tempo_previsto: number;
  tempo_real: number;
  concluida: boolean;
}

export interface OrdemProducaoCusto {
  custo_mp_previsto: number;
  custo_mp_real: number;
  custo_mdo_previsto: number;
  custo_mdo_real: number;
  custo_indiretos_previsto: number;
  custo_indiretos_real: number;
}

export interface OrdemProducaoQualidade {
  id: string;
  tipo: string;
  resultado: string;
  data_inspecao: string | null;
  responsavel: string | null;
  observacoes: string | null;
}

export interface OrdemProducao {
  id: string;
  numero: string;
  produto_id: string;
  produto?: { id: string; codigo: string; descricao: string } | null;
  produto_nome?: string | null;
  produto_descricao?: string | null;
  produto_codigo?: string | null;
  quantidade_planejada: number;
  quantidade_produzida: number;
  lote_producao: string | null;
  data_inicio_planejada: string | null;
  data_fim_planejada: string | null;
  data_inicio_real?: string | null;
  data_fim_real?: string | null;
  status: OrdemProducaoStatus;
  responsavel_id?: string | null;
  responsavel_nome?: string | null;
  observacoes?: string | null;
  materiais?: OrdemProducaoMaterial[];
  operacoes?: OrdemProducaoOperacao[];
  custo?: OrdemProducaoCusto | null;
  qualidade?: OrdemProducaoQualidade[];
}

export interface OrdemProducaoInput {
  numero: string;
  produto_id: string;
  quantidade_planejada: number;
  quantidade_produzida?: number;
  lote_producao?: string | null;
  data_inicio_planejada?: string | null;
  data_fim_planejada?: string | null;
  responsavel_id?: string | null;
  observacoes?: string | null;
}

export interface OrdemProducaoLiberarResult {
  op: string;
  status: string;
  necessidades: { produto: string; quantidade: number; unidade: string }[];
}

export interface OrdemProducaoEncerrarResult {
  op: string;
  status: string;
  lote: string;
  quantidade_produzida: number;
}

const ENDPOINT = '/industrializacao/ordem-producao';

const ordemProducaoService = {
  async listar(filters: { status?: string; produto?: string; responsavel?: string; search?: string } = {}): Promise<OrdemProducao[]> {
    const response = await api.get<OrdemProducao[]>(ENDPOINT, { params: filters });
    return response.data;
  },
  async buscarPorId(id: string): Promise<OrdemProducao> {
    const response = await api.get<OrdemProducao>(`${ENDPOINT}/${id}`);
    return response.data;
  },
  async criar(data: OrdemProducaoInput): Promise<OrdemProducao> {
    const response = await api.post<OrdemProducao>(ENDPOINT, {
      ...data,
      produto_id: nullableUuid(data.produto_id) || '',
      responsavel_id: nullableUuid(data.responsavel_id),
    });
    return response.data;
  },
  async atualizar(id: string, data: Partial<OrdemProducaoInput>): Promise<OrdemProducao> {
    const payload = {
      ...data,
      ...('produto_id' in data ? { produto_id: nullableUuid(data.produto_id) || '' } : {}),
      ...('responsavel_id' in data ? { responsavel_id: nullableUuid(data.responsavel_id) } : {}),
    };
    const response = await api.put<OrdemProducao>(`${ENDPOINT}/${id}`, payload);
    return response.data;
  },
  async excluir(id: string): Promise<void> {
    await api.delete(`${ENDPOINT}/${id}`);
  },
  /** Libera OP para produção (reserva estoque + MRP) */
  async liberar(id: string): Promise<OrdemProducaoLiberarResult> {
    const response = await api.post<OrdemProducaoLiberarResult>(`${ENDPOINT}/${id}/liberar`);
    return response.data;
  },
  /** Inicia produção da OP */
  async iniciar(id: string, dataInicio: string): Promise<OrdemProducao> {
    const response = await api.post<OrdemProducao>(`${ENDPOINT}/${id}/iniciar`, { data_inicio: dataInicio });
    return response.data;
  },
  /** Pausa produção */
  async pausar(id: string): Promise<OrdemProducao> {
    const response = await api.post<OrdemProducao>(`${ENDPOINT}/${id}/pausar`);
    return response.data;
  },
  /** Encerra OP (gera entrada de PA) */
  async encerrar(id: string, dataFim: string, quantidadeProduzidaFinal: number): Promise<OrdemProducaoEncerrarResult> {
    const response = await api.post<OrdemProducaoEncerrarResult>(`${ENDPOINT}/${id}/encerrar`, {
      data_fim: dataFim,
      quantidade_produzida_final: quantidadeProduzidaFinal,
    });
    return response.data;
  },
  /** Cancela OP */
  async cancelar(id: string, motivo: string): Promise<OrdemProducao> {
    const response = await api.post<OrdemProducao>(`${ENDPOINT}/${id}/cancelar`, { motivo });
    return response.data;
  },
};

export default ordemProducaoService;
